/**
 * WorkspaceSettingSecurity - Entry point for workspace security settings
 *
 * sl-qsjm.5: Routes between cloud (SecurityPanel) and local (not available).
 */
import { SettingHeader } from '@affine/component/setting-components';
import { AffineErrorBoundary } from '@affine/core/components/affine/affine-error-boundary';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import type { ReactElement } from 'react';

import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import { SecurityPanel } from './security-panel';

export const WorkspaceSettingSecurity = (): ReactElement | null => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const workspace = useService(WorkspaceService).workspace;
  const header = (
    <SettingHeader
      title={
        t['com.streamlain.settings.workspace.security.title']?.() ?? 'Security'
      }
      action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
    />
  );

  if (workspace.flavour === 'local') {
    return (
      <>
        {header}
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: 'var(--streamlain-text-secondary-color)',
          }}
        >
          Security settings are only available for cloud workspaces.
        </div>
      </>
    );
  }

  return (
    <>
      {header}
      <AffineErrorBoundary>
        <SecurityPanel />
      </AffineErrorBoundary>
    </>
  );
};
